import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Modal } from "../../primitives/Modal";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import {
  listProductBundleItems,
  getProduct,
  simulatePrice,
  listPromotions,
  type Product,
  type ProductBundleItemOut,
  type Promotion,
} from "../../../api/catalog";
import {
  addQuoteBundle,
  dissociateQuoteBundle,
  type QuoteBundleLineAdd,
  type QuoteBundleOut,
  type QuoteLineOut,
} from "../../../api/quotes";

const DELIVERY_DATES_PREFIX = "Dates de livraison: ";
const PERIODES_PREFIX = "Périodes de livraison: ";
const TOLERANCE_PREFIX = "Tolérance: ";
const PROMOTION_PREFIX = "Promotion: ";

const MONTH_NAMES = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juill.", "août", "sept.", "oct.", "nov.", "déc."];

/** Période = une demi-mois (1-15 ou 16-fin). month 1-12, half 1 ou 2. */
export type DeliveryPeriod = { month: number; half: 1 | 2 };

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(value);
}

function periodToStartDate(year: number, p: DeliveryPeriod): string {
  const day = p.half === 1 ? "01" : "16";
  const m = String(p.month).padStart(2, "0");
  return `${year}-${m}-${day}`;
}

function periodToEndDate(year: number, p: DeliveryPeriod): string {
  const lastDay = new Date(year, p.month, 0).getDate();
  const endDay = p.half === 1 ? 15 : lastDay;
  const m = String(p.month).padStart(2, "0");
  return `${year}-${m}-${String(endDay).padStart(2, "0")}`;
}

function addDaysToDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Tri chronologique annuel (janvier → décembre). */
function sortPeriodsChronological(periods: DeliveryPeriod[]): DeliveryPeriod[] {
  return [...periods].sort((a, b) => (a.month !== b.month ? a.month - b.month : a.half - b.half));
}

function parseNotesWithDeliveryDates(notes: string | null): {
  deliveryDates: string[];
  noteOnly: string;
  deliveryMode: "dates" | "timeline";
  toleranceDays: number;
  timelineYear: number;
  selectedPeriods: DeliveryPeriod[];
  selectedPromotionId: string | null;
} {
  const empty = {
    deliveryDates: [],
    noteOnly: notes ?? "",
    deliveryMode: "dates" as const,
    toleranceDays: 0,
    timelineYear: new Date().getFullYear(),
    selectedPeriods: [],
    selectedPromotionId: null as string | null,
  };
  if (!notes?.trim()) return empty;
  const lines = notes.split("\n").map((l) => l.trim());
  const first = lines[0] ?? "";
  if (first.startsWith(PERIODES_PREFIX)) {
    const rest = first.slice(PERIODES_PREFIX.length);
    const dateStrs = rest.split(",").map((s) => s.trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
    let timelineYear = new Date().getFullYear();
    const selectedPeriods: DeliveryPeriod[] = [];
    for (const d of dateStrs) {
      const [y, m, day] = d.split("-").map(Number);
      if (y && m >= 1 && m <= 12) {
        timelineYear = y;
        const half: 1 | 2 = day <= 15 ? 1 : 2;
        if (!selectedPeriods.some((p) => p.month === m && p.half === half)) {
          selectedPeriods.push({ month: m, half });
        }
      }
    }
    let selectedPromotionId: string | null = null;
    const noteLines: string[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (line?.startsWith(PROMOTION_PREFIX)) {
        const id = line.slice(PROMOTION_PREFIX.length).trim();
        if (id && !selectedPromotionId) selectedPromotionId = id;
      } else if (line) {
        noteLines.push(line);
      }
    }
    const noteOnly = noteLines.join("\n").trim();
    return {
      deliveryDates: [],
      noteOnly,
      deliveryMode: "timeline",
      toleranceDays: 0,
      timelineYear,
      selectedPeriods: sortPeriodsChronological(selectedPeriods),
      selectedPromotionId,
    };
  }
  if (!first.startsWith(DELIVERY_DATES_PREFIX)) return { ...empty, noteOnly: notes };
  const rest = first.slice(DELIVERY_DATES_PREFIX.length);
  const deliveryDates = rest
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
  let toleranceDays = 0;
  let selectedPromotionId: string | null = null;
  const noteLines: string[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line?.startsWith(TOLERANCE_PREFIX)) {
      const n = parseInt(line.slice(TOLERANCE_PREFIX.length).trim(), 10);
      if (!Number.isNaN(n) && n >= 0) toleranceDays = n;
    } else if (line?.startsWith(PROMOTION_PREFIX)) {
      const id = line.slice(PROMOTION_PREFIX.length).trim();
      if (id && !selectedPromotionId) selectedPromotionId = id;
    } else if (line) {
      noteLines.push(line);
    }
  }
  const noteOnly = noteLines.join("\n").trim();
  return {
    deliveryDates,
    noteOnly,
    deliveryMode: "dates",
    toleranceDays,
    timelineYear: new Date().getFullYear(),
    selectedPeriods: [],
    selectedPromotionId,
  };
}

function buildNotesWithDeliveryDates(
  deliveryMode: "dates" | "timeline",
  deliveryDates: string[],
  selectedPeriods: DeliveryPeriod[],
  timelineYear: number,
  toleranceDays: number,
  noteOnly: string,
  selectedPromotionId?: string | null
): string {
  const headerLines: string[] = [];
  if (deliveryMode === "timeline" && selectedPeriods.length > 0) {
    const dates = sortPeriodsChronological(selectedPeriods).map((p) => periodToStartDate(timelineYear, p));
    headerLines.push(PERIODES_PREFIX + dates.join(", "));
  } else if (deliveryMode === "dates" && deliveryDates.length > 0) {
    const valid = deliveryDates.filter((d) => d && /^\d{4}-\d{2}-\d{2}$/.test(d));
    if (valid.length > 0) {
      headerLines.push(DELIVERY_DATES_PREFIX + valid.join(", "));
      if (toleranceDays > 0) headerLines.push(TOLERANCE_PREFIX + String(toleranceDays));
    }
  }
  if (selectedPromotionId) headerLines.push(PROMOTION_PREFIX + selectedPromotionId);
  const header = headerLines.join("\n");
  if (!header) return noteOnly;
  return noteOnly ? header + "\n\n" + noteOnly : header;
}

type ConfigureBundleModalProps = {
  open: boolean;
  bundle: Product | null;
  quoteId: string | null;
  /** When set, modal is in edit mode: prefill from this bundle and on save replace it (dissociate + add). */
  editBundle?: QuoteBundleOut | null;
  /** Lines of the bundle when editing (to prefill quantities). */
  existingLines?: QuoteLineOut[];
  onClose: () => void;
  onSaved: () => void;
};

type LineState = {
  component_product_id: string;
  label: string;
  product_type: string;
  unit_kind: string | null;
  tax_code: string | null;
  quantity: number;
  unit_price: number;
  is_required: boolean;
};

export function ConfigureBundleModal({
  open,
  bundle,
  quoteId,
  editBundle = null,
  existingLines = [],
  onClose,
  onSaved,
}: ConfigureBundleModalProps) {
  const [items, setItems] = useState<ProductBundleItemOut[]>([]);
  const [lineStates, setLineStates] = useState<LineState[]>([]);
  const [configName, setConfigName] = useState("");
  const [deliveryDates, setDeliveryDates] = useState<string[]>([]);
  const [deliveryMode, setDeliveryMode] = useState<"dates" | "timeline">("dates");
  const [toleranceDays, setToleranceDays] = useState(0);
  const [timelineYear, setTimelineYear] = useState(() => new Date().getFullYear());
  const [selectedPeriods, setSelectedPeriods] = useState<DeliveryPeriod[]>([]);
  const [note, setNote] = useState("");
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [selectedPromotionId, setSelectedPromotionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!editBundle?.id;

  const loadBundleData = useCallback(async () => {
    if (!bundle?.id || !open) return;
    setLoading(true);
    setError(null);
    try {
      const [bundleItemsRes, ...rest] = await Promise.all([
        listProductBundleItems(bundle.id),
        Promise.resolve(),
      ]);
      const bundleItems = bundleItemsRes;
      if (!bundleItems.length) {
        setItems([]);
        setLineStates([]);
        setLoading(false);
        return;
      }
      const componentIds = [...new Set(bundleItems.map((i) => i.component_product_id))];
      const [productsRes, pricesRes] = await Promise.all([
        Promise.all(componentIds.map((id) => getProduct(id).catch(() => null))),
        Promise.all(
          bundleItems.map((item) =>
            simulatePrice({
              product_id: item.component_product_id,
              quantity: item.qty_used ?? 1,
            }).then((r) => ({ id: item.component_product_id, unit_price: r.unit_price ?? 0 }))
          )
        ),
      ]);
      const priceMap: Record<string, number> = {};
      for (const r of pricesRes) {
        priceMap[r.id] = r.unit_price;
      }
      const productMap: Record<string, { product_type: string; unit_kind: string | null; tax_code: string | null }> = {};
      for (let i = 0; i < componentIds.length; i++) {
        const p = productsRes[i];
        if (p) {
          productMap[componentIds[i]] = {
            product_type: (p as Product).product_type ?? "service",
            unit_kind: (p as Product).unit_kind ?? null,
            tax_code: (p as Product).tax_code ?? null,
          };
        } else {
          productMap[componentIds[i]] = { product_type: "service", unit_kind: null, tax_code: null };
        }
      }
      const lineQuantityByProductId: Record<string, number> = {};
      if (existingLines?.length) {
        for (const line of existingLines) {
          if (line.product_id) lineQuantityByProductId[line.product_id] = Math.max(0, Math.round(line.quantity));
        }
      }
      setItems(bundleItems);
      setLineStates(
        bundleItems.map((item) => {
          const defaultQty = item.is_required ? Math.max(1, Math.round(item.qty_used ?? 1)) : 0;
          const rawQty =
            existingLines?.length && lineQuantityByProductId[item.component_product_id] != null
              ? lineQuantityByProductId[item.component_product_id]
              : defaultQty;
          const qty = item.is_required
            ? Math.max(1, Math.round(rawQty))
            : Math.max(0, Math.round(rawQty));
          return {
            component_product_id: item.component_product_id,
            label: item.component_product_name,
            product_type: productMap[item.component_product_id]?.product_type ?? "service",
            unit_kind: productMap[item.component_product_id]?.unit_kind ?? null,
            tax_code: productMap[item.component_product_id]?.tax_code ?? null,
            quantity: qty,
            unit_price: priceMap[item.component_product_id] ?? 0,
            is_required: item.is_required,
          };
        })
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur chargement du bundle");
      setItems([]);
      setLineStates([]);
    } finally {
      setLoading(false);
    }
  }, [bundle?.id, open, existingLines]);

  useEffect(() => {
    if (open && bundle) {
      if (editBundle) {
        setConfigName(editBundle.name);
        const parsed = parseNotesWithDeliveryDates(editBundle.notes);
        setDeliveryDates(parsed.deliveryDates);
        setDeliveryMode(parsed.deliveryMode);
        setToleranceDays(parsed.toleranceDays);
        setTimelineYear(parsed.timelineYear);
        setSelectedPeriods(parsed.selectedPeriods);
        setNote(parsed.noteOnly);
        setSelectedPromotionId(parsed.selectedPromotionId);
      } else {
        setConfigName("");
        setDeliveryDates([]);
        setDeliveryMode("dates");
        setToleranceDays(0);
        setTimelineYear(new Date().getFullYear());
        setSelectedPeriods([]);
        setNote("");
        setSelectedPromotionId(null);
      }
      loadBundleData();
    }
  }, [open, bundle, editBundle, loadBundleData]);

  useEffect(() => {
    if (open) {
      listPromotions({ status_filter: "active" })
        .then((list) => setPromotions(list))
        .catch(() => setPromotions([]));
    }
  }, [open]);

  const togglePeriod = (month: number, half: 1 | 2) => {
    setSelectedPeriods((prev) => {
      const key = `${month}-${half}`;
      const has = prev.some((p) => p.month === month && p.half === half);
      if (has) return prev.filter((p) => !(p.month === month && p.half === half));
      return sortPeriodsChronological([...prev, { month, half }]);
    });
  };

  const isPeriodSelected = (month: number, half: 1 | 2) =>
    selectedPeriods.some((p) => p.month === month && p.half === half);

  const updateQuantity = (componentProductId: string, quantity: number) => {
    setLineStates((prev) =>
      prev.map((l) => {
        if (l.component_product_id !== componentProductId) return l;
        const qty = Math.max(0, Math.round(quantity));
        const final = l.is_required ? Math.max(1, qty) : qty;
        return { ...l, quantity: final };
      })
    );
  };

  const toggleOptional = (componentProductId: string, selected: boolean) => {
    setLineStates((prev) =>
      prev.map((l) => {
        if (l.component_product_id !== componentProductId || l.is_required) return l;
        return { ...l, quantity: selected ? Math.max(1, Math.round(l.quantity || 1)) : 0 };
      })
    );
  };

  const requiredLines = lineStates.filter((l) => l.is_required && l.quantity > 0);
  const optionalLines = lineStates.filter((l) => !l.is_required);
  const selectedOptionalLines = optionalLines.filter((l) => l.quantity > 0);
  const linesToSubmit = [...requiredLines, ...selectedOptionalLines];

  const bundleTotal = linesToSubmit.reduce((sum, l) => sum + l.quantity * l.unit_price, 0);

  const handleSave = async () => {
    if (!quoteId || !bundle || linesToSubmit.length === 0) return;
    const nameToUse = (configName || bundle.name).trim() || bundle.name;
    setSaving(true);
    setError(null);
    try {
      if (isEditMode && editBundle) {
        await dissociateQuoteBundle(quoteId, editBundle.id);
      }
      const notesFinal = buildNotesWithDeliveryDates(
        deliveryMode,
        deliveryDates,
        selectedPeriods,
        timelineYear,
        toleranceDays,
        note.trim(),
        selectedPromotionId
      );
      const payload = {
        bundle_product_id: bundle.id,
        name: nameToUse,
        notes: notesFinal || null,
        lines: linesToSubmit.map(
          (l): QuoteBundleLineAdd => ({
            product_id: l.component_product_id,
            label: l.label,
            product_type: l.product_type,
            quantity: l.quantity,
            unit_kind: l.unit_kind,
            unit_price: l.unit_price,
            tax_code: l.tax_code,
          })
        ),
      };
      await addQuoteBundle(quoteId, payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'ajout du bundle");
    } finally {
      setSaving(false);
    }
  };

  if (!bundle) return null;

  return (
    <Modal
      title={isEditMode ? `Modifier la configuration : ${bundle.name}` : `Configurer le bundle : ${bundle.name}`}
      open={open}
      onClose={onClose}
      showDefaultButtons={false}
      showCloseButton={true}
      size="2xl"
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Chargement des éléments du bundle…</span>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted py-6 text-center">
            Ce bundle n&apos;a pas d&apos;éléments configurés (obligatoires ou optionnels). Configurez-les dans le catalogue Produits &amp; services.
          </p>
        ) : (
          <>
            {/* Nom de la configuration */}
            <div>
              <label className="block text-sm font-semibold text-text mb-1">Nom de la configuration</label>
              <Input
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                placeholder={bundle.name}
                className="w-full"
              />
            </div>

            {/* 1. Items obligatoires - tableau */}
            {items.some((i) => i.is_required) && (
              <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-3">
                <h3 className="text-sm font-semibold text-text mb-1">Éléments obligatoires</h3>
                <p className="text-xs text-muted mb-2">Validez les quantités (ex. superficie, quantité).</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-muted border-b border-border">
                        <th className="py-2 pr-2 font-medium">Élément</th>
                        <th className="py-2 pr-2 text-right w-20">Qté</th>
                        <th className="py-2 pr-2 text-right w-28">Prix</th>
                        <th className="py-2 text-right w-28">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineStates
                        .filter((l) => l.is_required)
                        .map((l) => (
                          <tr key={l.component_product_id} className="border-b border-border/50">
                            <td className="py-2 pr-2 font-semibold text-text">
                              {l.label}
                              {l.unit_kind && <span className="font-normal text-muted ml-1">({l.unit_kind})</span>}
                            </td>
                            <td className="py-2 pr-2 text-right">
                              <input
                                type="number"
                                min={1}
                                step={1}
                                value={l.quantity}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  updateQuantity(l.component_product_id, Number.isNaN(v) ? 1 : v);
                                }}
                                className="w-20 rounded border border-border bg-surface px-2 py-1 text-right text-sm"
                              />
                            </td>
                            <td className="py-2 pr-2 text-right tabular-nums text-muted">
                              {formatCurrency(l.unit_price)}
                            </td>
                            <td className="py-2 text-right tabular-nums font-semibold text-text">
                              {formatCurrency(l.quantity * l.unit_price)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 2. Items optionnels - tableau */}
            {optionalLines.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/10 p-3">
                <h3 className="text-sm font-semibold text-text mb-1">Éléments optionnels</h3>
                <p className="text-xs text-muted mb-2">Cochez les options souhaitées et indiquez la quantité.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-muted border-b border-border">
                        <th className="py-2 pr-2 w-8" />
                        <th className="py-2 pr-2 font-medium">Élément</th>
                        <th className="py-2 pr-2 text-right w-20">Qté</th>
                        <th className="py-2 pr-2 text-right w-28">Prix</th>
                        <th className="py-2 text-right w-28">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optionalLines.map((l) => (
                        <tr key={l.component_product_id} className="border-b border-border/50">
                          <td className="py-2 pr-2 align-middle">
                            <input
                              type="checkbox"
                              checked={l.quantity > 0}
                              onChange={(e) => toggleOptional(l.component_product_id, e.target.checked)}
                              className="h-4 w-4 rounded border-border text-primary"
                            />
                          </td>
                          <td className="py-2 pr-2 text-text">{l.label}</td>
                          <td className="py-2 pr-2 text-right">
                            {l.quantity > 0 ? (
                              <input
                                type="number"
                                min={1}
                                step={1}
                                value={l.quantity}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  updateQuantity(l.component_product_id, Number.isNaN(v) ? 1 : v);
                                }}
                                className="w-20 rounded border border-border bg-surface px-2 py-1 text-right text-sm"
                              />
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-2 pr-2 text-right tabular-nums text-muted">
                            {formatCurrency(l.unit_price)}
                          </td>
                          <td className="py-2 text-right tabular-nums">
                            {l.quantity > 0 ? formatCurrency(l.quantity * l.unit_price) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 3. Dates de livraison */}
            <div className="rounded-lg border border-border bg-muted/10 p-3 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="block text-sm font-semibold text-text">Dates de livraison</label>
                <div className="flex rounded-lg border border-border overflow-hidden bg-surface">
                  <button
                    type="button"
                    onClick={() => setDeliveryMode("dates")}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${deliveryMode === "dates" ? "bg-primary text-white" : "text-muted hover:bg-muted/50"}`}
                  >
                    Dates précises
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliveryMode("timeline")}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${deliveryMode === "timeline" ? "bg-primary text-white" : "text-muted hover:bg-muted/50"}`}
                  >
                    Ligne du temps
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted">Choisissez des dates précises (avec option de tolérance) ou des périodes par demi-mois.</p>

              {deliveryMode === "dates" ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-text">Tolérance ±</span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={toleranceDays}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setToleranceDays(Number.isNaN(v) || v < 0 ? 0 : v);
                      }}
                      className="w-16 rounded border border-border bg-surface px-2 py-1 text-sm text-right"
                    />
                    <span className="text-sm text-muted">jours (0 = date exacte)</span>
                  </div>
                  <ul className="space-y-2 mb-2">
                    {deliveryDates.map((d, idx) => {
                      const valid = /^\d{4}-\d{2}-\d{2}$/.test(d);
                      const start = valid && toleranceDays > 0 ? addDaysToDate(d, -toleranceDays) : d;
                      const end = valid && toleranceDays > 0 ? addDaysToDate(d, toleranceDays) : d;
                      return (
                        <li key={idx} className="flex items-center gap-2 flex-wrap">
                          <input
                            type="date"
                            value={d}
                            onChange={(e) =>
                              setDeliveryDates((prev) => {
                                const next = [...prev];
                                next[idx] = e.target.value;
                                return next;
                              })
                            }
                            className="rounded border border-border bg-surface px-2 py-1.5 text-sm"
                          />
                          {valid && toleranceDays > 0 && (
                            <span className="text-xs text-muted">
                              → {start} à {end}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setDeliveryDates((prev) => prev.filter((_, i) => i !== idx))}
                            className="p-1 text-muted hover:text-danger rounded"
                            aria-label="Supprimer la date"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setDeliveryDates((prev) => [...prev, ""])}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter une date de livraison
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-muted">Année :</span>
                    <input
                      type="number"
                      min={2020}
                      max={2030}
                      value={timelineYear}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) setTimelineYear(v);
                      }}
                      className="w-20 rounded border border-border bg-surface px-2 py-1 text-sm"
                    />
                  </div>
                  <p className="text-xs text-muted mb-2">Cliquez sur une période pour la sélectionner (1-15 ou 16-fin du mois).</p>
                  <div className="overflow-x-auto pb-2">
                    <div className="flex gap-1 min-w-max py-2 px-1">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                        <div key={month} className="flex flex-col items-center gap-1 w-14 flex-shrink-0">
                          <div className="text-center text-xs font-semibold text-muted uppercase tracking-wider truncate w-full">{MONTH_NAMES[month - 1]}</div>
                          <div className="flex flex-col gap-1 w-full">
                            <button
                              type="button"
                              onClick={() => togglePeriod(month, 1)}
                              className={`h-9 rounded-md border-2 text-xs font-medium transition-all ${isPeriodSelected(month, 1) ? "bg-primary text-white border-primary shadow-md" : "bg-surface border-border hover:border-primary/40 hover:bg-muted/30 text-text"}`}
                              title={`${MONTH_NAMES[month - 1]} 1 au 15`}
                            >
                              1-15
                            </button>
                            <button
                              type="button"
                              onClick={() => togglePeriod(month, 2)}
                              className={`h-9 rounded-md border-2 text-xs font-medium transition-all ${isPeriodSelected(month, 2) ? "bg-primary text-white border-primary shadow-md" : "bg-surface border-border hover:border-primary/40 hover:bg-muted/30 text-text"}`}
                              title={`${MONTH_NAMES[month - 1]} 16 à fin`}
                            >
                              16-fin
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {selectedPeriods.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="text-xs font-medium text-muted mb-1">Périodes sélectionnées (ordre chronologique)</div>
                      <ul className="text-sm text-text space-y-0.5">
                        {sortPeriodsChronological(selectedPeriods).map((p, i) => (
                          <li key={`${p.month}-${p.half}-${i}`}>
                            {MONTH_NAMES[p.month - 1]} {p.half === 1 ? "1–15" : "16–" + new Date(timelineYear, p.month, 0).getDate()} ({periodToStartDate(timelineYear, p)} → {periodToEndDate(timelineYear, p)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 4. Note */}
            <div>
              <label className="block text-sm font-semibold text-text mb-2">Note pour ce bundle</label>
              <textarea
                placeholder="Note enregistrée pour ce bundle dans la soumission…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full min-h-[80px] rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                rows={3}
              />
            </div>

            {/* 5. Promotions */}
            <div className="rounded-lg border border-border bg-muted/5 p-3">
              <label className="block text-sm font-semibold text-text mb-2">Promotions</label>
              {promotions.length === 0 ? (
                <p className="text-sm text-muted">Aucune promotion en cours.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted mb-1">Optionnel : appliquer une promotion à ce bundle.</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="promo-bundle"
                      checked={selectedPromotionId === null}
                      onChange={() => setSelectedPromotionId(null)}
                      className="rounded-full border-border text-primary"
                    />
                    <span className="text-sm text-text">Aucune</span>
                  </label>
                  {promotions.map((promo) => (
                    <label key={promo.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="promo-bundle"
                        checked={selectedPromotionId === promo.id}
                        onChange={() => setSelectedPromotionId(promo.id)}
                        className="rounded-full border-border text-primary"
                      />
                      <span className="text-sm text-text">
                        {promo.name}
                        {(promo as { promotion_type?: string; value?: number })?.promotion_type === "percentage" && (promo as { value?: number }).value != null
                          ? ` (-${(promo as { value: number }).value} %)`
                          : (promo as { promotion_type?: string; value?: number })?.promotion_type === "fixed_amount" && (promo as { value?: number }).value != null
                            ? ` (-${(promo as { value: number }).value} $)`
                            : ""}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 6. Résumé du prix */}
            <div className="rounded-lg border border-border bg-muted/10 p-4">
              <h3 className="text-sm font-semibold text-text mb-2">Résumé du prix</h3>
              <div className="overflow-x-auto rounded-md border border-border bg-surface">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/30 text-left text-muted">
                      <th className="py-2 pr-2 font-medium">Élément</th>
                      <th className="py-2 pr-2 text-right w-20">Qté</th>
                      <th className="py-2 pr-2 text-right w-28">Prix unit.</th>
                      <th className="py-2 text-right w-28">Total</th>
                    </tr>
                  </thead>
                  <tbody className="text-text">
                    {linesToSubmit.map((l) => (
                      <tr key={l.component_product_id} className="border-b border-border/50 hover:bg-muted/20">
                        <td className="py-2 pr-2 truncate">{l.label}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{l.quantity}</td>
                        <td className="py-2 pr-2 text-right tabular-nums text-muted">{formatCurrency(l.unit_price)}</td>
                        <td className="py-2 text-right tabular-nums font-medium">{formatCurrency(l.quantity * l.unit_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/20 font-semibold text-text">
                      <td className="py-2 pr-2" colSpan={3}>Total du bundle</td>
                      <td className="py-2 text-right tabular-nums">{formatCurrency(bundleTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="rounded-md border border-danger bg-danger/10 p-3 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} />
          <Button
            label={saving ? "Enregistrement…" : isEditMode ? "Enregistrer les modifications" : "Enregistrer le bundle"}
            intent="primary"
            size="sm"
            onClick={handleSave}
            state={saving ? "loading" : "default"}
            disabled={loading || linesToSubmit.length === 0}
          />
        </div>
      </div>
    </Modal>
  );
}
